/**
 * Telemetry Event Tiers
 *
 * CRITICAL: Always tracked, triggers push notifications to platform admins
 * - Security events (auth failures, session revocations)
 * - Error events (client errors, API failures)
 * - Platform admin actions (user management, org changes)
 *
 * ANALYTICS: Toggle-able, used for understanding user behavior
 * - Page views, tab switches, filter changes
 * - Search queries, navigation patterns
 * - Feature usage metrics
 */

export type TelemetryTier = "critical" | "analytics";

export interface TelemetryEventConfig {
  tier: TelemetryTier;
  pushNotify?: boolean;
  sampleRate?: number;
}

// Critical event types that should always be tracked and trigger push notifications
export const criticalEventTypes: Record<string, TelemetryEventConfig> = {
  // Auth/Security events
  login_failure: { tier: "critical", pushNotify: true },
  login_success: { tier: "critical", pushNotify: false },
  logout: { tier: "critical", pushNotify: false },
  session_revoked: { tier: "critical", pushNotify: true },
  password_reset_requested: { tier: "critical", pushNotify: true },
  password_changed: { tier: "critical", pushNotify: false },
  mfa_failure: { tier: "critical", pushNotify: true },

  // Error events
  client_error: { tier: "critical", pushNotify: true },
  api_error: { tier: "critical", pushNotify: true },
  error_page_view: { tier: "critical", pushNotify: true },
  error_report_submitted: { tier: "critical", pushNotify: true },

  // Platform admin actions
  user_created: { tier: "critical", pushNotify: false },
  user_deleted: { tier: "critical", pushNotify: true },
  user_disabled: { tier: "critical", pushNotify: true },
  user_enabled: { tier: "critical", pushNotify: false },
  org_disabled: { tier: "critical", pushNotify: true },
  org_enabled: { tier: "critical", pushNotify: false },
  impersonation_started: { tier: "critical", pushNotify: true },

  // Rate limiting
  rate_limit_exceeded: { tier: "critical", pushNotify: true },

  // Service reliability events (spec §2)
  ros_timeout: { tier: "critical", pushNotify: true },
  ros_upstream_failure: { tier: "critical", pushNotify: true },
  ros_validation_error: { tier: "critical", pushNotify: false },
  planning_timeout: { tier: "critical", pushNotify: true },
  planning_query_failure: { tier: "critical", pushNotify: true },
  matrix_timeout: { tier: "critical", pushNotify: true },
  matrix_query_failure: { tier: "critical", pushNotify: true },
  dependency_timeout: { tier: "critical", pushNotify: true },
  retry_exhausted: { tier: "critical", pushNotify: true },
  service_error_rate_critical: { tier: "critical", pushNotify: true },
  service_timeout_rate_critical: { tier: "critical", pushNotify: true },
  service_latency_p95_critical: { tier: "critical", pushNotify: true },
};

// Analytics event types that can be toggled
export const analyticsEventTypes: Record<string, TelemetryEventConfig> = {
  // Navigation
  page_view: { tier: "analytics", sampleRate: 0.5 },
  tab_switch: { tier: "analytics", sampleRate: 0.5 },
  view_mode_change: { tier: "analytics", sampleRate: 1 },

  // Search & filters
  search_query: { tier: "analytics", sampleRate: 0.2 },
  search_zero_results: { tier: "analytics", sampleRate: 1 },
  filter_applied: { tier: "analytics", sampleRate: 0.3 },

  // User actions
  dialog_opened: { tier: "analytics", sampleRate: 0.5 },
  dialog_submitted: { tier: "analytics", sampleRate: 1 },
  button_click: { tier: "analytics", sampleRate: 0.2 },
  export_requested: { tier: "analytics", sampleRate: 1 },

  // CRUD operations (always track these for audit)
  project_create: { tier: "analytics", sampleRate: 1 },
  project_update: { tier: "analytics", sampleRate: 1 },
  project_delete: { tier: "analytics", sampleRate: 1 },
  project_status_change: { tier: "analytics", sampleRate: 1 },
  task_create: { tier: "analytics", sampleRate: 1 },
  task_update: { tier: "analytics", sampleRate: 1 },
  task_delete: { tier: "analytics", sampleRate: 1 },
  task_status_change: { tier: "analytics", sampleRate: 1 },

  // Shell / global-nav (high-volume — sampled). Other shell actions
  // (quick_add_open/quick_add_select/org_switch/support_ticket_open/sign_out)
  // are low-volume + business-meaningful and use the default full sampleRate.
  nav_click: { tier: "analytics", sampleRate: 0.3 },
  command_palette_open: { tier: "analytics", sampleRate: 0.5 },

  // Profile/directory
  profile_view: { tier: "analytics", sampleRate: 0.5 },

  // Favorites/preferences
  report_favorited: { tier: "analytics", sampleRate: 1 },
  report_unfavorited: { tier: "analytics", sampleRate: 1 },

  // Service reliability analytics (spec §2.3 — dashboard data)
  service_request_completed: { tier: "analytics", sampleRate: 1 },
  service_view_loaded: { tier: "analytics", sampleRate: 1 },
  service_retry_attempted: { tier: "analytics", sampleRate: 1 },
};

export function getEventConfig(eventType: string): TelemetryEventConfig {
  return (
    criticalEventTypes[eventType] ||
    analyticsEventTypes[eventType] || { tier: "analytics", sampleRate: 1 }
  );
}

export function isCriticalEvent(eventType: string): boolean {
  return eventType in criticalEventTypes;
}

export function shouldPushNotify(eventType: string): boolean {
  return criticalEventTypes[eventType]?.pushNotify === true;
}

// Legacy sampling config (kept for backward compatibility)
export const telemetrySampling = {
  search: {
    searchRate: 0.2,
    selectRate: 0.2,
    dedupeWindowMs: 20000,
  },
  dashboard: {
    widgetRate: 0.2,
  },
  platform: {
    sampleRate: 1,
    criticalEventTypes: ["auth", "admin_action", "password_reset"],
  },
};

// Storage key for analytics telemetry toggle
export const ANALYTICS_TELEMETRY_ENABLED_KEY = "forge:telemetry:analytics:enabled";

// Storage key for frequent/detailed telemetry collection (admin toggle)
export const FREQUENT_TELEMETRY_ENABLED_KEY = "forge:telemetry:frequent:enabled";

/**
 * Check if analytics telemetry is enabled (stored in localStorage)
 * Critical telemetry is always enabled regardless of this setting.
 */
export function isAnalyticsTelemetryEnabled(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const stored = window.localStorage.getItem(ANALYTICS_TELEMETRY_ENABLED_KEY);
    // Default to enabled if not set
    return stored !== "false";
  } catch {
    return true;
  }
}

/**
 * Check if frequent/detailed telemetry collection is enabled.
 * When enabled, sampling rates are set to 1.0 for all events.
 * Default is OFF to reduce telemetry noise.
 */
export function isFrequentTelemetryEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(FREQUENT_TELEMETRY_ENABLED_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * Set frequent/detailed telemetry collection enabled/disabled
 */
export function setFrequentTelemetryEnabled(enabled: boolean): void {
  if (typeof window === "undefined") return;
  try {
    if (enabled) {
      window.localStorage.setItem(FREQUENT_TELEMETRY_ENABLED_KEY, "true");
    } else {
      window.localStorage.removeItem(FREQUENT_TELEMETRY_ENABLED_KEY);
    }
  } catch {
    // Ignore storage errors
  }
}

/**
 * Set analytics telemetry enabled/disabled
 */
export function setAnalyticsTelemetryEnabled(enabled: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ANALYTICS_TELEMETRY_ENABLED_KEY, String(enabled));
  } catch {
    // Ignore storage errors
  }
}
